import * as querystring from 'querystring';
import moment from 'moment-timezone';

import { Entitlement } from '../beneficiaries/model';
import { DateSelectFilter, Filter, FilterTypes } from '../utils/FilterTypes';
import { PagedState } from '../utils/paging';
import { Transaction, TransactionChain } from '../transactions/transactions';
import { BigNumber } from '../utils/bigNumber';
import { TransactionRequestParams } from '../apiClient';
import { transformDateFilters } from '../../utils/transformDateFilters';
import { getProjectUrl } from '../app/appConfig';
import { windowStorage, WindowStorageToken } from '../windowStorage';
import { SORT_OPTIONS } from '../utils/hooks/useSort';

export const tooLongValueError = new Error('Given value is too long');

const BaseUrl = () => {
    return `${getProjectUrl()}api/partner`;
};

export function getWithPaging<T>(
    url: string,
    page: number,
    limit: number,
    customParams: Record<string, unknown> = {}
): Promise<PagedState<T>> {
    const offset = (page - 1) * limit;
    return getWithOffset(url, offset, limit, customParams);
}

async function getWithOffset<T>(
    url: string,
    offset: number,
    limit: number,
    customParams: Record<string, unknown> = {}
): Promise<PagedState<T>> {
    const params = Object.assign({}, { _offset: offset, _limit: limit });

    Object.assign(params, customParams);
    const queryString = querystring.stringify(params);

    const res = (await get(`${url}?${queryString}`)) as PagedState<T> & { status?: number };
    if (res.status === 204) {
        return new PagedState<T>().configureFor204Response(limit, []);
    }
    return res as PagedState<T>;
}

function authHeaders() {
    const stored = windowStorage.getItem(WindowStorageToken.partnerUserApiToken) || '';
    if (!stored) {
        return { Authorization: '' };
    }
    const token = stored.replace(/^Bearer\s+/i, '');
    const header = /^Bearer\s+/i.test(stored) ? stored : `Bearer ${token}`;
    return {
        Authorization: header,
    };
}

function jsonHeaders() {
    return new Headers(
        Object.assign(
            {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            authHeaders()
        )
    );
}

function fetch(url, requestInit) {
    return window.fetch(url, requestInit).then((response) => {
        if (response.status < 200 || response.status >= 300) {
            throw response;
        }
        if (response.status !== 204) {
            return response.json();
        }
        return response;
    });
}

function get(url: string): Promise<unknown> {
    return fetch(url, {
        method: 'GET',
        headers: jsonHeaders(),
    });
}

function post(url: string, body: unknown): Promise<unknown> {
    return fetch(url, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify(body),
    });
}

function assignFiltersToParams(filters: Array<Filter>, params: { _offset: number; _limit: number }) {
    filters.forEach((f) => {
        if (f.isSelected) {
            if (f instanceof DateSelectFilter) {
                const dateFilter = f as DateSelectFilter;
                const startDateString = moment(dateFilter.dateRange.startDate).tz('utc').format();
                const endDateString = moment(dateFilter.dateRange.endDate).tz('utc').format();
                params[f.name + ':gte'] = startDateString;
                params[f.name + ':lte'] = endDateString;
            } else if (f.type === FilterTypes.multiselect) {
                const values = f.value
                    ? (f.value as Array<{ value: unknown }>).map((selectOption) => selectOption.value)
                    : [];
                params[f.name] = values.toString();
            } else {
                params[f.name] = f.value;
            }
        }
    });
}

async function postWithOffset<T>(
    url: string,
    filters: Array<Filter>,
    offset: number,
    limit: number,
    customBody: Record<string, unknown> = {}
): Promise<PagedState<T>> {
    const body = Object.assign({}, { _offset: offset, _limit: limit });
    assignFiltersToParams(filters, body);
    Object.assign(body, customBody);

    const res = (await post(url, body)) as PagedState<T> & { status?: number };
    if (res.status === 204) {
        return new PagedState<T>().configureFor204Response(limit, filters);
    }
    return res as PagedState<T>;
}

function postWithPaging<T>(
    url: string,
    filters: Array<Filter>,
    page: number,
    limit: number,
    customParams: Record<string, unknown> = {}
): Promise<PagedState<T>> {
    const offset = (page - 1) * limit;
    return postWithOffset(url, filters, offset, limit, customParams);
}

export function getAppConfig() {
    return get(`${BaseUrl()}/app-config`);
}

// Auth endpoints should not include authorization headers
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function authFetch(url, options = {}): Promise<any> {
    return window
        .fetch(url, {
            ...options,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        })
        .then((response) => {
            if (response.status < 200 || response.status >= 300) {
                throw response;
            }
            if (response.status !== 204) {
                return response.json();
            }
            return response;
        });
}

export const partnerAuthLogin = (returnUrl?: string) => {
    const params = new URLSearchParams();
    if (returnUrl) {
        params.append('returnUrl', returnUrl);
    }
    params.append('target', 'partner');
    const url = `${getProjectUrl()}api/auth/login?${params.toString()}`;
    return authFetch(url);
};

export const partnerAuthVerify = (state: string, code: string) =>
    authFetch(`${getProjectUrl()}api/auth/verify`, {
        method: 'POST',
        body: JSON.stringify({ code, state }),
    });

export const partnerAuthRefresh = () => authFetch(`${getProjectUrl()}api/auth/refresh`, { method: 'POST' });

export const partnerAuthLogout = () => authFetch(`${getProjectUrl()}api/auth/logout`, { method: 'POST' });

export function loadBeneficiary(id: string) {
    return post(`${BaseUrl()}/beneficiaries/beneficiary`, { beneficiaryId: id });
}

export async function getEntitlements(beneficiaryId: string, pageNumber: number, pageSize: number) {
    const url = `${BaseUrl()}/beneficiaries/beneficiary/entitlements`;
    const response = await postWithPaging<Entitlement>(url, [], pageNumber, pageSize, { beneficiaryId });
    return response;
}

export async function getClustersInfo(beneficiaryId: string) {
    const url = `${BaseUrl()}/beneficiaries/beneficiary/token-clusters`;
    return post(url, { beneficiaryId });
}

export function loadVendors(query) {
    const searchparams = Object.entries(query).reduce(
        (object, [key, value]) =>
            value
                ? {
                      ...object,
                      [key]: value,
                  }
                : object,
        {}
    );
    const querystring = new URLSearchParams(searchparams);
    return get(`${BaseUrl()}/vendors?${querystring}`);
}

export function loadVendorsNickNames(query: SORT_OPTIONS) {
    const searchparams = Object.entries(query).reduce(
        (object, [key, value]) =>
            value
                ? {
                      ...object,
                      [key]: value,
                  }
                : object,
        {}
    );
    const querystring = new URLSearchParams(searchparams);
    return get(`${BaseUrl()}/vendors/nick-names?${querystring}`);
}

export function loadTransactions(
    timezone: string,
    filters: Array<Filter>,
    page: number,
    limit: number,
    customParams: TransactionRequestParams = {}
): Promise<PagedState<Transaction>> {
    const _filters = filters.map(transformDateFilters(timezone));
    const paged = postWithPaging<Transaction>(
        `${BaseUrl()}/transactions/list`,
        _filters,
        page,
        limit,
        customParams as Record<string, unknown>
    );
    return paged.then((result) => {
        result.items.forEach((transaction) => (transaction.amount = new BigNumber(transaction.amount)));
        return result;
    });
}

export function loadTransactionChain(transactionId: string): Promise<TransactionChain> {
    return get(`${BaseUrl()}/transactions/${transactionId}`).then((result) => {
        const chain = result as TransactionChain;
        chain.amount = new BigNumber(chain.amount);
        chain.items.forEach((transaction) => {
            transaction.amount = new BigNumber(transaction.amount);
        });
        return chain;
    });
}

export function loadTransactionDetails(transactionId: string) {
    return get(`${BaseUrl()}/transactions/${transactionId}/details`);
}

export function healthCheck() {
    return get(`${BaseUrl()}/health-check`);
}

export function getBeneficiariesTransactionStatistics(filters) {
    const query = new URLSearchParams(filters).toString();
    return get(`${BaseUrl()}/statistics/mobilization-statistics?${query}`);
}

export function getTodayBeneficiariesTransactionStatistics(filters) {
    const query = new URLSearchParams(filters).toString();
    return get(`${BaseUrl()}/statistics/mobilization-statistics/today?${query}`);
}

export function getMobilizationStatisticsSyncDate() {
    return get(`${BaseUrl()}/statistics/mobilization-statistics/sync-date`);
}

export function getBranchCodes() {
    return get(`${BaseUrl()}/branch-codes`);
}
